// @ts-nocheck
import React, { useState, useRef, useEffect } from "react";
import PendingDescriptionBanner, { readPendingStructure, hasPendingCarry } from '@/components/CreateOrganization/PendingDescriptionBanner';
import { useNavigate } from "react-router-dom";
import CreateOnboardingShell from './CreateOnboardingShell';
import WizardStepPill from './WizardStepPill';
import { useLocationSearch } from '@/components/CreateOrganization/useLocationSearch';
import { toast } from 'sonner';
import { useAuth, useUser } from '@/auth/clerkShim';
import useAdminStore from '@store/adminStore';
import { useMinWidth } from '@/hooks/use-min-width';
import { isTouchDevice } from '@/utils/shareLink';

export default function CreateContestTypePage() {
  const navigate = useNavigate();
  const [showExplanation, setShowExplanation] = useState(false);
  const [locationError, setLocationError] = useState('');
  // 'button' shows the "Use my location" affordance; 'hidden' when permission is
  // denied or geolocation is unsupported (clicking could never succeed).
  const [geoAffordance, setGeoAffordance] = useState(
    typeof navigator !== 'undefined' && navigator.geolocation ? 'button' : 'hidden'
  );
  const inputRef = useRef<HTMLInputElement>(null);
  // INT-0011 tier 2: the carried preview already implies league vs
  // tournament — highlight that card instead of re-asking cold. The user can
  // still pick either; their click wins.
  const [suggestedType] = useState(() => {
    const pending = readPendingStructure();
    if (!pending) return null;
    return pending.contestType === 'tournament' ? 'tournament' : 'league';
  });

  const {
    location,
    suggestions,
    showSuggestions,
    isSearching,
    isDetecting,
    isSearchUnavailable,
    detectLocation,
    highlightedIndex,
    setHighlightedIndex,
    suggestionsRef,
    handleInputChange,
    handleSelectSuggestion,
    handleKeyDown,
    handleBlur,
    handleFocus,
  } = useLocationSearch('');

  // Autofill with NO surprise permission popup: only detect on mount when the
  // browser permission is already granted (silent). Otherwise the user opts in
  // via the "Use my location" button, so the prompt rides a real click.
  useEffect(() => {
    if (!navigator.geolocation) return;
    navigator.permissions?.query({ name: 'geolocation' })
      .then((status) => {
        if (status.state === 'granted') detectLocation();
        else if (status.state === 'denied') setGeoAffordance('hidden');
      })
      .catch(() => { /* permissions API missing — keep the button */ });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSelectType = (type: 'league' | 'tournament') => {
    // The location seeds the public site's "Where we play" map post — without
    // it every new org gets the generic default. Require it before moving on.
    if (!location.trim()) {
      setLocationError('Please enter your location first');
      inputRef.current?.focus();
      return;
    }
    const params = new URLSearchParams();
    // URLSearchParams percent-encodes once; step 2 reads it with .get() (INT-0038
    // dropped the double encoding that broke on literal '%' and accents).
    params.set('location', location.trim());
    if (type === 'tournament') {
      params.set('tournament', '1');
    }
    navigate(`/create/create-organization?${params.toString()}`);
  };

  const onLocationChange = (e) => {
    if (locationError) setLocationError('');
    handleInputChange(e);
  };

  const onPickSuggestion = (suggestion) => {
    if (locationError) setLocationError('');
    handleSelectSuggestion(suggestion);
  };

  // Phone hand-off (INT-0038): ~40% of signups are on phones and ~12% of
  // them return; a plain /setup link to their inbox lets them finish on a
  // computer. Shown on touch devices or anything narrower than 1024px.
  const { getToken } = useAuth();
  const { user } = useUser();
  const wide = useMinWidth(1024);
  const showHandoff = isTouchDevice() || !wide;
  const [sendingHandoff, setSendingHandoff] = useState(false);
  const sendHandoff = async () => {
    if (sendingHandoff) return;
    setSendingHandoff(true);
    try {
      const send = useAdminStore.getState().sendHandoffEmail(getToken);
      const result = await send();
      toast.success(`Sent to ${result?.sentTo || user?.primaryEmailAddress?.emailAddress || 'your email'}.`);
    } catch (err) {
      const status = err?.response?.status;
      toast.error(status === 429 ? 'Already sent a moment ago. Check your inbox.' : 'Could not send the email. Try again in a moment.');
    } finally {
      setSendingHandoff(false);
    }
  };

  return (
    <CreateOnboardingShell>
      <main className="relative z-10 flex flex-1 flex-col items-center justify-center px-6 pb-16 pt-4">
        <div className="w-full max-w-[520px]">
          {/* Hero text */}
          <div className="mb-8 text-center">
            <WizardStepPill step={1} />
            <h1 className="text-3xl font-semibold tracking-tight text-foreground sm:text-4xl">
              Let's set up your organization
            </h1>
            <p className="mt-3 text-base text-muted-foreground">
              First, tell us where you're located
            </p>
          </div>

          {/* INT-0011: show the landing carry-through so the visitor knows
              their description is still following them. */}
          <PendingDescriptionBanner />

          {/* Location Input */}
          <div className="mb-10">
            <label htmlFor="location" className="mb-2 block text-sm font-medium text-foreground">
              Location
            </label>
            <div className="relative">
              <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-4">
                <svg className="h-5 w-5 text-muted-foreground/60" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
              </div>
              <input
                ref={inputRef}
                type="text"
                id="location"
                value={location}
                onChange={onLocationChange}
                onKeyDown={handleKeyDown}
                onFocus={handleFocus}
                onBlur={handleBlur}
                placeholder="e.g., Los Angeles, CA"
                autoComplete="off"
                className={`w-full rounded-xl border-0 bg-card py-3.5 pl-12 pr-12 text-foreground shadow-lg ring-1 placeholder:text-muted-foreground/60 focus:ring-2 focus:ring-offset-0 ${
                  locationError
                    ? 'ring-destructive focus:ring-destructive'
                    : 'ring-border focus:ring-ring'
                }`}
              />
              {(isSearching || isDetecting) ? (
                <div className="absolute inset-y-0 right-0 flex items-center pr-4">
                  <svg className="h-4 w-4 animate-spin text-muted-foreground/60" viewBox="0 0 24 24" fill="none">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                </div>
              ) : geoAffordance === 'button' && !isSearchUnavailable && (
                <button
                  type="button"
                  onClick={detectLocation}
                  title="Use my location"
                  data-tour="create-location-detect"
                  aria-label="Use my location"
                  className="absolute inset-y-0 right-0 flex items-center pr-4 text-muted-foreground/60 transition-colors hover:text-foreground"
                >
                  {/* crosshair / locate icon */}
                  <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <circle cx="12" cy="12" r="7" strokeWidth={1.5} />
                    <circle cx="12" cy="12" r="2.5" strokeWidth={1.5} />
                    <path strokeLinecap="round" strokeWidth={1.5} d="M12 2v3M12 19v3M2 12h3M19 12h3" />
                  </svg>
                </button>
              )}

              {/* Autocomplete Dropdown */}
              {showSuggestions && suggestions.length > 0 && (
                <div
                  ref={suggestionsRef}
                  className="absolute left-0 right-0 top-full z-50 mt-2 overflow-hidden rounded-xl bg-card shadow-xl ring-1 ring-border"
                >
                  {suggestions.map((suggestion, index) => (
                    <button
                      key={suggestion.id}
                      type="button"
                      onClick={() => onPickSuggestion(suggestion)}
                      onMouseEnter={() => setHighlightedIndex(index)}
                      className={`flex w-full items-center gap-3 px-4 py-3 text-left transition-colors ${
                        index === highlightedIndex
                          ? 'bg-muted'
                          : 'hover:bg-muted/50'
                      }`}
                    >
                      <svg className="h-4 w-4 flex-shrink-0 text-muted-foreground/60" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                      </svg>
                      <span className="text-sm font-medium text-foreground">
                        {suggestion.display}
                      </span>
                    </button>
                  ))}
                </div>
              )}
            </div>
            {locationError ? (
              <p className="mt-2 text-xs font-medium text-destructive">{locationError}</p>
            ) : (
              <p className="mt-2 text-xs text-muted-foreground/60">
                City where you play. This seeds the map on your site.
              </p>
            )}
          </div>

          {/* Divider */}
          <div className="mb-8 flex items-center gap-4">
            <div className="h-px flex-1 bg-border"></div>
            <span className="text-sm font-medium text-muted-foreground/60">Choose your contest type</span>
            <div className="h-px flex-1 bg-border"></div>
          </div>

          {/* Options */}
          <div className="grid gap-4 sm:grid-cols-2">
            {/* League Option */}
            <button
              type="button"
              data-tour="create-type-league"
              onClick={() => handleSelectType('league')}
              className={`group relative flex flex-col items-center rounded-2xl bg-card p-6 text-center shadow-lg ring-1 transition-all hover:shadow-xl active:scale-[0.98] ${
                suggestedType === 'league' ? 'ring-2 ring-primary' : 'ring-border hover:ring-input'
              }`}
            >
              {suggestedType === 'league' && (
                <span className="absolute -top-2.5 rounded-full bg-primary px-3 py-0.5 text-[11px] font-semibold text-primary-foreground shadow-sm">
                  Suggested from your description
                </span>
              )}
              <div className="mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-gradient-to-br from-primary to-primary-deep text-primary-foreground shadow-lg shadow-primary/20">
                <svg className="h-7 w-7" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                </svg>
              </div>
              <h3 className="text-lg font-semibold text-foreground">League</h3>
              <p className="mt-1.5 text-sm text-muted-foreground">
                Recurring seasons with standings, schedules & playoffs
              </p>
              <div className="mt-4 flex items-center gap-1 text-xs font-medium text-muted-foreground/60 transition-colors group-hover:text-muted-foreground">
                Get started
                <svg className="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </div>
            </button>

            {/* Tournament Option */}
            <button
              type="button"
              data-tour="create-type-tournament"
              onClick={() => handleSelectType('tournament')}
              className={`group relative flex flex-col items-center rounded-2xl bg-card p-6 text-center shadow-lg ring-1 transition-all hover:shadow-xl active:scale-[0.98] ${
                suggestedType === 'tournament' ? 'ring-2 ring-primary' : 'ring-border hover:ring-input'
              }`}
            >
              {suggestedType === 'tournament' && (
                <span className="absolute -top-2.5 rounded-full bg-primary px-3 py-0.5 text-[11px] font-semibold text-primary-foreground shadow-sm">
                  Suggested from your description
                </span>
              )}
              <div className="mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-gradient-to-br from-warning to-warning/80 text-warning-foreground shadow-lg shadow-warning/20">
                <svg className="h-7 w-7" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
                </svg>
              </div>
              <h3 className="text-lg font-semibold text-foreground">Tournament</h3>
              <p className="mt-1.5 text-sm text-muted-foreground">
                Single event with brackets, pools & elimination rounds
              </p>
              <div className="mt-4 flex items-center gap-1 text-xs font-medium text-muted-foreground/60 transition-colors group-hover:text-muted-foreground">
                Get started
                <svg className="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </div>
            </button>
          </div>

          {showHandoff && (
            <div className="mt-6 flex flex-col items-center gap-2 rounded-xl bg-muted/50 px-4 py-3 text-center sm:flex-row sm:justify-between sm:text-left">
              <p className="text-sm text-muted-foreground">On your phone? Email yourself a link to finish on a computer.</p>
              <button
                type="button"
                onClick={sendHandoff}
                disabled={sendingHandoff}
                data-tour="create-handoff-email"
                className="shrink-0 rounded-lg border border-border bg-card px-3 py-1.5 text-sm font-medium text-foreground shadow-sm transition-colors hover:bg-muted disabled:opacity-60"
              >
                {sendingHandoff ? 'Sending' : 'Email me the link'}
              </button>
            </div>
          )}

          {/* Future note */}
          <div className="mt-6 rounded-xl bg-muted/50 px-4 py-3 text-center">
            <p className="text-sm text-muted-foreground">
              <svg className="mr-1.5 inline-block h-4 w-4 text-muted-foreground/60" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              You can always create both leagues and tournaments later
            </p>
          </div>

          {/* Help text */}
          <p className="mt-6 text-center text-sm text-muted-foreground/60">
            Not sure which to pick?{' '}
            <button
              type="button"
              onClick={() => setShowExplanation(true)}
              className="font-medium text-muted-foreground underline-offset-2 hover:underline"
            >
              Learn the difference
            </button>
          </p>
        </div>
      </main>

      {/* Explanation Modal */}
      {showExplanation && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          {/* Backdrop */}
          <div
            className="absolute inset-0 bg-foreground/50 backdrop-blur-sm"
            onClick={() => setShowExplanation(false)}
          />

          {/* Modal */}
          <div className="relative w-full max-w-lg rounded-2xl bg-card p-6 shadow-2xl">
            {/* Close button */}
            <button
              type="button"
              onClick={() => setShowExplanation(false)}
              className="absolute right-4 top-4 rounded-lg p-1.5 text-muted-foreground/60 transition-colors hover:bg-muted hover:text-muted-foreground"
            >
              <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>

            <h2 className="mb-6 text-xl font-semibold text-foreground">
              League vs Tournament
            </h2>

            {/* League explanation */}
            <div className="mb-6 flex gap-4">
              <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-primary to-primary-deep text-primary-foreground">
                <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                </svg>
              </div>
              <div>
                <h3 className="font-semibold text-foreground">League (Season-based)</h3>
                <p className="mt-1 text-sm text-muted-foreground">
                  Think of your local basketball or soccer league. Teams play regularly over weeks or months, 
                  building up standings. At the end of the season, top teams may compete in playoffs. 
                  You can run multiple seasons (Spring, Summer, Fall) under the same league.
                </p>
                <p className="mt-2 text-xs text-muted-foreground/60">
                  Best for: recreational leagues, club sports, intramural programs
                </p>
              </div>
            </div>

            {/* Tournament explanation */}
            <div className="mb-6 flex gap-4">
              <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-warning to-warning/80 text-warning-foreground">
                <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
                </svg>
              </div>
              <div>
                <h3 className="font-semibold text-foreground">Tournament (Event-based)</h3>
                <p className="mt-1 text-sm text-muted-foreground">
                  A one-time or annual event where teams compete over a short period, often a single weekend. 
                  Teams are eliminated through brackets or advance through pool play until a champion is crowned. 
                  Each tournament is a standalone event.
                </p>
                <p className="mt-2 text-xs text-muted-foreground/60">
                  Best for: championship events, fundraisers, weekend competitions
                </p>
              </div>
            </div>

            {/* Summary */}
            <div className="rounded-xl bg-muted/50 p-4">
              <p className="text-sm text-muted-foreground">
                <strong className="text-foreground">Quick tip:</strong> If you're running an ongoing program 
                where teams play week-to-week, choose <strong>League</strong>. If you're hosting a 
                single competition event, choose <strong>Tournament</strong>.
              </p>
            </div>

            <button
              type="button"
              onClick={() => setShowExplanation(false)}
              className="mt-6 w-full rounded-xl bg-primary py-3 text-sm font-semibold text-primary-foreground transition-colors hover:bg-primary/90"
            >
              Got it
            </button>
          </div>
        </div>
      )}
    </CreateOnboardingShell>
  );
}
